import ProductHelpersMixin from "../../mixins/ProductHelpersMixin"
import CartHelpersMixin from "../../mixins/CartHelpersMixin"
import { currencyFormat, notify, successNotify, errorNotify } from "../../functions"
import store from "../../store"

export default {
  mixins: [
    CartHelpersMixin,
    ProductHelpersMixin
  ],
  data() {
    return {
      shippingMethodName: null,
      paymentMethodName: null,
      crossSellProducts: [],
      currencyFormat,
      notify,
      successNotify,
      errorNotify,
      popoverTimers: {},
      debounceTimers: {},
      pendingCartUpdates: 0,
      isWaitingForCheckout: false
    }
  },
  mounted() {
    // Initialize showPop for each cart item
    Object.values(store.state.cart.items).forEach(item => {
      this.$set(item, 'showPop', false)
    })
  },
  computed: {
    hasAnyCrossSellProduct() {
      return this.crossSellProducts.length !== 0
    }
  },
  created() {
    this.$nextTick(() => {
      if (store.state.cart.shippingMethodName) {
        this.changeShippingMethod(store.state.cart.shippingMethodName)
      } else {
        this.updateShippingMethod(this.firstShippingMethod)
      }

      if (store.state.cart.paymentMethodName) {
        this.changePaymentMethod(store.state.cart.paymentMethodName)
      } else {
        this.updatePaymentMethod(this.firstPaymentMethod)
      }
    })
  },
  methods: {
    optionValues(option) {
      let values = []

      for (let value of option.values) {
        values.push(value.label)
      }

      return values.join(", ")
    },
    updateQuantity(cartItem, qty) {
      // Handle empty or invalid input
      if (qty === '' || qty === 0 || isNaN(qty)) {
        qty = 1
      }
      
      // Convert to integer
      qty = parseInt(qty)
      
      // Update UI immediately for responsive feel
      this.checkMaxQty(qty, cartItem)
      
      // Store the final qty value to prevent race conditions
      const finalQty = cartItem.qty
      const itemId = cartItem.id
      
      // Debounce per item - each item has its own timer
      this.debouncePerItem(itemId, () => {
        this.storeCart(cartItem, finalQty)
      }, 500)
    },
    updatePrice(cartItem, price) {
      store.updateCart(cartItem, price)
    },
    remove(cartItem) {
      this.loadingOrderSummary = true

      store.removeCartItem(cartItem)

      if (store.cartIsEmpty()) {
        this.crossSellProducts = []
      }

      $.ajax({
        method: "DELETE",
        url: route("cart.items.destroy", { cartItemId: cartItem.id })
      }).then((cart) => {
        store.updateCart(cart)
      }).catch((xhr) => {
        this.$errorNotify(xhr.responseJSON.message)
      }).always(() => {
        this.loadingOrderSummary = false
      })
    },
    clearCart() {
      store.clearCart()

      if (store.cartIsEmpty()) {
        this.crossSellProducts = []
      }

      $.ajax({
        method: "POST",
        url: route("cart.clear.store")
      }).then((cart) => {
        store.updateCart(cart)
      }).catch((xhr) => {
        this.$errorNotify(xhr.responseJSON.message)
      })
    },
    changeShippingMethod(shippingMethodName) {
      this.shippingMethodName = shippingMethodName
    },
    changePaymentMethod(paymentMethodName) {
      this.paymentMethodName = paymentMethodName
    },
    getMaxSellingQty(cartItem) {
      const product = cartItem.product
      
      // If sb_enabled is true, use min of available_qty and qty_in_stock
      // If sb_enabled is false, use qty_in_stock
      if (product.sb_enabled) {
        return Math.min(product.available_qty || 0, product.qty_in_stock || 0)
      }
      
      return product.qty_in_stock || 0
    },
    checkMaxQty(qty, cartItem) {
      const maxQty = this.getMaxSellingQty(cartItem)
      
      if (qty < 1 || this.exceedsMaxStock(qty, maxQty)) {
        cartItem.qty = maxQty
        // Store the item ID to persist popover state across cart updates
        const itemId = cartItem.id
        this.$set(cartItem, 'showPop', true)
        
        // Use a flag to track if popover should stay visible
        if (!this.popoverTimers) {
          this.popoverTimers = {}
        }
        
        // Clear existing timer for this item
        if (this.popoverTimers[itemId]) {
          clearTimeout(this.popoverTimers[itemId])
        }
        
        // Set new timer
        this.popoverTimers[itemId] = setTimeout(() => {
          // Find the cart item again after potential updates
          const currentItem = store.state.cart.items[itemId]
          if (currentItem) {
            this.$set(currentItem, 'showPop', false)
          }
          delete this.popoverTimers[itemId]
        }, 3000)
      } else {
        if (isNaN(qty)) {
          qty = 1
        }
        cartItem.qty = parseInt(qty)
        this.$set(cartItem, 'showPop', false)
      }
    },
    exceedsMaxStock(qty, maxQty) {
      return maxQty < qty
    },
    debouncePerItem(itemId, callback, delay) {
      // Clear existing timer for this specific item
      if (this.debounceTimers[itemId]) {
        clearTimeout(this.debounceTimers[itemId])
      }
      
      // Set new timer for this item
      this.debounceTimers[itemId] = setTimeout(() => {
        callback()
        delete this.debounceTimers[itemId]
      }, delay)
    },
    hasPendingUpdates() {
      // Check if there are active debounce timers or pending AJAX requests
      const hasDebounceTimers = Object.keys(this.debounceTimers).length > 0
      const hasPendingAjax = this.pendingCartUpdates > 0
      return hasDebounceTimers || hasPendingAjax
    },
    async waitForAllUpdates() {
      // Wait for all debounce timers to complete
      while (Object.keys(this.debounceTimers).length > 0) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }
      
      // Wait for all AJAX requests to complete
      while (this.pendingCartUpdates > 0) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }
    },
    async handleCheckoutClick(event) {
      // Prevent double clicks
      if (this.isWaitingForCheckout) {
        event.preventDefault()
        return
      }
      
      // Check if there are pending updates
      if (this.hasPendingUpdates()) {
        event.preventDefault()
        this.isWaitingForCheckout = true
        this.loadingOrderSummary = true
        
        // Wait for all updates to complete
        await this.waitForAllUpdates()
        
        // Now proceed to checkout
        this.proceedToCheckout()
      }
      // If no pending updates, let the default link behavior work
    },
    proceedToCheckout() {
      this.isWaitingForCheckout = false
      this.loadingOrderSummary = false
      // Navigate to checkout page
      window.location.href = route('checkout.create')
    },
    storeCart(cartItem, qty) {
      if (qty < 1) {
        return
      }

      if (isNaN(qty)) {
        qty = 1
      }

      this.loadingOrderSummary = true
      this.pendingCartUpdates++
      
      const maxQty = this.getMaxSellingQty(cartItem)
      qty = qty > maxQty ? maxQty : qty
      cartItem.qty = qty

      $.ajax({
        method: "PUT",
        url: route("cart.items.update", { cartItemId: cartItem.id }),
        data: { qty: qty || 1 }
      }).then((cart) => {
        // Preserve popover state and current qty before cart update
        const itemId = cartItem.id
        const shouldShowPop = cartItem.showPop
        const currentQty = cartItem.qty
        
        store.updateCart(cart)
        
        // Restore popover state and qty after cart update
        if (store.state.cart.items[itemId]) {
          this.$set(store.state.cart.items[itemId], 'showPop', shouldShowPop || false)
          // Keep the user's input value if they're still typing
          this.$set(store.state.cart.items[itemId], 'qty', currentQty)
        }
      }).catch((xhr) => {
        errorNotify(xhr.responseJSON.message)
      }).always(() => {
        this.loadingOrderSummary = false
        this.pendingCartUpdates--
        
        // If checkout is waiting and all updates are done, proceed
        if (this.isWaitingForCheckout && !this.hasPendingUpdates()) {
          this.proceedToCheckout()
        }
      })
    },
    handleInput(cartItem, value) {
      // Sanitize the input: if it's empty or not a number, set to '0'
      const sanitizedValue = value.trim() === "" || isNaN(parseFloat(value)) ? "0" : value

      // Update the local component state (which is bound to the input field)
      cartItem.unitPrice.inCurrentCurrency.formatted = sanitizedValue

      // Optionally, call storeCartPrice or any other method if you need to perform additional actions
      this.storeCartPrice(cartItem, sanitizedValue)
    },
    updateMSP(product, newValue) {
      if (newValue < product.cost.inCurrentCurrency.formatted)
        return alert("MSP can not be lower than cost (" + product.cost.inCurrentCurrency.formatted + ")")
      else {
        this.updateProduct(product.id, { msp: newValue })
      }
    },
    updateSB(product, newValue) {
      this.updateProduct(product.id, { sb_remaining_qty: newValue })
    },
    updateProduct(productId, data) {
      this.loadingOrderSummary = true
      $.ajax({
        method: "PUT",
        url: route("cart.product.update", { id: productId }),
        data
      }).then(() => {
        successNotify("Product data updated successfully")
      }).catch((xhr) => {
        console.log("error message", xhr.responseJSON.message)
        alert(xhr.responseJSON.message)
      }).always(() => {
        this.loadingOrderSummary = false
      })
    },
    storeCartPrice(cartItem, price) {
      this.loadingOrderSummary = true
      $.ajax({
        method: "PUT",
        url: route("cart.items.update", { cartItemId: cartItem.id }),
        data: { price: price }
      }).then((cart) => {
        store.updateCart(cart)
        errorNotify("Product price updated successfully")
      }).catch((xhr) => {
        console.log("error message", xhr.responseJSON.message)
        errorNotify(xhr.responseJSON.message)
      }).always(() => {
        this.loadingOrderSummary = false
      })
    }
  }
}
